
"use client";

import React, { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "./context/LanguageContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import Link from "next/link";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// ---------- TYPES ----------
type Lang = "en" | "fr" | "ar";

interface Specialite {
  id: number;
  title: string;
  Slug: string;

  description: string;
  cover?: string;
}

export default function SpecialtiesPage() {
  const [specialites, setSpecialites] = useState<Specialite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { language } = useLanguage() as { language: Lang };
  const { theme } = useTheme();

  const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL;

  // ---------- TRANSLATIONS ----------
  const translations: Record<Lang, { title: string; subtitle: string ;buttoon :string}> = {
    en: {
      title: "Our Specialties",
      subtitle:
        "An overview of our products that help you achieve perfect results",
      buttoon: "See more specialties → ",

    },
    fr: {
      title: "Nos Spécialités",
      subtitle:
        "Un aperçu de nos produits pour vous aider à obtenir des résultats parfaits",
              buttoon: "Voir  plus Specialties → ",

    },
    
    ar: {
      title: "تخصصاتنا",
      subtitle:
        "نظرة عامة على منتجاتنا التي تساعدك على الحصول على أفضل النتائج",
              buttoon: "عرض المزيد من التخصصات →" ,

    },
  };

  const isRTL = language === "ar";

  // ---------- FETCH DATA ----------the old one without api token
  /*
  const fetchSpecialites = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${STRAPI_URL}/api/categoryspecialitesses?populate=*&locale=${language}`,
        { cache: "no-store" }
      );

      if (!res.ok) throw new Error("Fetch failed");

      const json = await res.json();

      const mapped: Specialite[] = json.data
        .sort((a: any, b: any) => (a.Order ?? 0) - (b.Order ?? 0))
        .map((item: any) => {
          // Extract text from Rich Text
          const description =
            item.Description?.map((block: any) =>
              block.children?.map((c: any) => c.text).join("")
            ).join(" ") || "";

          const coverUrl = item.cover?.formats?.large?.url
            ? `${STRAPI_URL}${item.cover.formats.large.url}`
            : item.cover?.url
            ? `${STRAPI_URL}${item.cover.url}`
            : "/images/default.jpg";

          return {
            id: item.id,
            title: item.Name || "",
            description,
            cover: coverUrl,
             Slug: item.Slug,      // 🔥 Correct!

          };
 
        });

      setSpecialites(mapped);
 
    } catch (err) {
      console.error(err);
      setError("Failed loading specialties");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchSpecialites();
  }, [language]);
 
useEffect(() => {
  console.log("specialites updated =>", specialites);
}, [specialites]);
*/ 

  // ---------- FETCH DATA ----------the new one without api token

const fetchSpecialites = async () => {
  setLoading(true);
  setError(null);

  try {
    const res = await fetch(`/api/specialties?locale=${language}`, { cache: "no-store" });
    if (!res.ok) throw new Error("Fetch failed");

    const json = await res.json();

    const mapped: Specialite[] = json.data
      .sort((a: any, b: any) => (a.Order ?? 0) - (b.Order ?? 0))
      .map((item: any) => {
        const description =
          item.Description?.map((block: any) =>
            block.children?.map((c: any) => c.text).join("")
          ).join(" ") || "";

        const coverUrl = item.cover?.formats?.large?.url
          ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${item.cover.formats.large.url}`
          : item.cover?.url
          ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${item.cover.url}`
          : "/images/default.jpg";

        return {
          id: item.id,
          title: item.Name || "",
          description,
                Slug: item.Slug,          // ✅ ADD THIS
          cover: coverUrl,
        };
      });

    setSpecialites(mapped);
  } catch (err) {
    console.error(err);
    setError("Failed loading specialties");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchSpecialites();
  }, [language]);




  // ---------- STATES ----------
  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-gray-600 dark:text-gray-300">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-24 text-red-600">
        {error}
      </div>
    );
  }

  // ---------- UI ----------
  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`${
        theme === "dark" ? "bg-gray-900" : "bg-white"
      }`}
    >
      {/* Header */}
      <div className="w-full py-20">
        <div className="max-w-6xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-semibold text-gray-800 dark:text-white">
            {translations[language].title}
          </h2>

          <p className="mt-6 text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {translations[language].subtitle}
          </p>
        </div>
      </div>

      {/* Slider */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        <Swiper
          modules={[Pagination, Navigation]}
          pagination={{ clickable: true }}
          navigation
          spaceBetween={20}
          dir={isRTL ? "rtl" : "ltr"}
          breakpoints={{
            320: { slidesPerView: 1.1 },
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 4 },
          }}
          className="pb-12"
        >
          {specialites.map((sp) => (
            <SwiperSlide key={sp.id}>
              <div className="rounded-xl overflow-hidden shadow hover:shadow-xl transition cursor-pointer relative bg-white dark:bg-gray-800">
<Link
    key={sp.id}
    href={`/Specialties/${sp.Slug}`}
    className="block"
  >
               <img
                  src={sp.cover}
                  alt={sp.title}
                  className="w-full h-64 object-cover"
                />

                <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-black/70 to-transparent flex items-center px-4">
                  <span className="text-white text-sm font-medium truncate">
   {sp.title}
 
                  
                  </span>
                </div>
                </Link>

              </div>
            </SwiperSlide>
          ))}
        </Swiper>
               <div className="text-center mt-12">
            <Link href="/Specialties">
              <button
                className={`  px-6  py-3 rounded-xl font-semibold ${
                  theme === "dark" ? "bg-blue-600 text-white" : "bg-gray-900 text-white"
                }`}
              >
            {translations[language].buttoon}
              </button>
            </Link>
          </div>
      </div>
    </div>
  );
}



 